import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionBaseComponent,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken,
  DwUiConfigRegistryService
} from '@devwareapps/devware-cap';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil } from 'rxjs/operators';

import {
  AppMetaDataItemNames,
  AssignmentCourseLessonEntity,
  AssignmentEntity,
  AssignmentStudentEntity
} from '../../../../meta-data/app-meta-data.service';
import { AssignmentStudentEligibility } from '../../models/assignment-student-eligibility.model';
import { AssignmentResetImpact } from '../../models/assignment-reset-impact.model';
import { AssignmentResetImpactService } from '../../services/assignment-reset-impact.service';
import { AssignmentStudentEligibilityService } from '../../services/assignment-student-eligibility.service';

interface AssignmentFooterStatusViewModel {
  readonly statusLabel: string;
  readonly statusTone: 'ready' | 'draft' | 'blocked';
  readonly lessonCount: number;
  readonly studentCount: number;
  readonly showSelectionCounts: boolean;
  readonly ignoredStudentCount: number;
  readonly studentCountTitle: string;
  readonly missingRequiredFieldsText: string;
  readonly showRequiredFieldsWarning: boolean;
  readonly showResetWarning: boolean;
  readonly resetWarningText: string;
}

@DwComponent({
  key: 'assignment-form-footer-status',
  name: 'Assignment Form Footer Status',
  componentType: DwComponentType.containerFooterSummary,
  parentItemName: AppMetaDataItemNames.Assignment,
  isGlobal: false
})
@Component({
  selector: 'app-assignment-form-footer-status',
  templateUrl: './assignment-form-footer-status.component.html',
  styleUrls: ['./assignment-form-footer-status.component.scss']
})
export class AssignmentFormFooterStatusComponent extends DwSectionBaseComponent implements OnInit, OnDestroy {
  formApi: DwMetaDataFormApi;
  viewModel: AssignmentFooterStatusViewModel;

  private readonly destroy$ = new Subject<void>();
  private studentEligibility: AssignmentStudentEligibility;
  private resetImpact: AssignmentResetImpact;

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    private dwFormStateService: DwMetaDataFormStateService,
    private assignmentStudentEligibilityService: AssignmentStudentEligibilityService,
    private assignmentResetImpactService: AssignmentResetImpactService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.updateViewModel(this.formApi.getFormData<AssignmentEntity>());

    this.runAfterFormDataLoaded(() => {
      const assignment = this.formApi.getFormData<AssignmentEntity>();

      this.updateViewModel(assignment);
      this.assignmentStudentEligibilityService.evaluate(assignment)
        .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
        .subscribe(eligibility => {
          this.studentEligibility = eligibility;
          this.updateViewModel(assignment);
        });
    });

    this.formApi.getFormDataChanges<AssignmentEntity>()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(assignment => this.updateViewModel(assignment));

    this.formApi.getFormDataChanges<AssignmentEntity>()
      .pipe(
        debounceTime(150),
        switchMap(assignment => this.assignmentStudentEligibilityService.evaluate(assignment)
          .pipe(catchError(() => of(null)))),
        takeUntil(this.destroy$)
      )
      .subscribe(eligibility => {
        this.studentEligibility = eligibility;
        this.updateViewModel(this.formApi.getFormData<AssignmentEntity>());
      });

    this.formGroup.statusChanges
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.updateViewModel(this.formApi.getFormData<AssignmentEntity>()));

    this.assignmentResetImpactService.observe(this.formApi)
      .pipe(takeUntil(this.destroy$))
      .subscribe(impact => {
        this.resetImpact = impact;
        this.updateViewModel(this.formApi.getFormData<AssignmentEntity>());
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateViewModel(assignment: AssignmentEntity): void {
    const lessonIds = this.resolveActiveLessonIds(assignment);
    const studentIds = this.resolveActiveStudentIds(assignment);
    const studentCount = this.studentEligibility?.courseSelected
      ? this.studentEligibility.validStudentCount
      : studentIds.length;
    const ignoredStudentCount = this.studentEligibility?.ignoredStudentCount || 0;
    const hasRequiredSelection = lessonIds.length > 0 && studentCount > 0;
    const isValid = this.formGroup.valid && hasRequiredSelection;
    const isPending = this.formGroup.pending;
    const statusTone = isValid ? 'ready' : hasRequiredSelection || isPending ? 'draft' : 'blocked';
    const resetImpactLoading = this.resetImpact?.IsLoading === true;
    const resetWillOccur = this.resetImpact?.WillResetProgress === true;
    const missingRequiredFields = hasRequiredSelection ? this.resolveMissingRequiredFields() : [];

    this.viewModel = {
      statusLabel: this.resolveStatusLabel(isValid, hasRequiredSelection, isPending),
      statusTone,
      lessonCount: lessonIds.length,
      studentCount,
      showSelectionCounts: hasRequiredSelection,
      ignoredStudentCount,
      studentCountTitle: this.formatStudentCountTitle(studentCount, ignoredStudentCount),
      missingRequiredFieldsText: missingRequiredFields.join(', '),
      showRequiredFieldsWarning: missingRequiredFields.length > 0,
      showResetWarning: hasRequiredSelection && !resetImpactLoading && resetWillOccur,
      resetWarningText: resetWillOccur ? this.formatResetWarning(this.resetImpact) : ''
    };
  }

  private formatStudentCountTitle(validStudentCount: number, ignoredStudentCount: number): string {
    const validLabel = validStudentCount === 1 ? '1 student will be assigned' : `${validStudentCount} students will be assigned`;

    if (ignoredStudentCount === 0) {
      return validLabel;
    }

    const ignoredLabel = ignoredStudentCount === 1
      ? '1 student will be ignored because they do not have the course'
      : `${ignoredStudentCount} students will be ignored because they do not have the course`;

    return `${validLabel}. ${ignoredLabel}.`;
  }

  private resolveActiveLessonIds(assignment: AssignmentEntity): number[] {
    return ((assignment?.AssignmentCourseLesson || []) as AssignmentCourseLessonEntity[])
      .filter(lesson => lesson._isDeleted !== true && !!lesson.CourseLessonId)
      .map(lesson => Number(lesson.CourseLessonId))
      .filter((courseLessonId, index, lessonIds) => lessonIds.indexOf(courseLessonId) === index)
      .sort((lessonId1, lessonId2) => lessonId1 - lessonId2);
  }

  private resolveActiveStudentIds(assignment: AssignmentEntity): number[] {
    return ((assignment?.AssignmentStudent || []) as AssignmentStudentEntity[])
      .filter(student => student._isDeleted !== true && !!student.StudentId)
      .map(student => Number(student.StudentId))
      .filter((studentId, index, studentIds) => studentIds.indexOf(studentId) === index)
      .sort((studentId1, studentId2) => studentId1 - studentId2);
  }

  private resolveStatusLabel(isValid: boolean, hasRequiredSelection: boolean, isPending: boolean): string {
    if (isPending) {
      return 'Checking form';
    }

    if (!hasRequiredSelection) {
      return 'Choose lessons and students';
    }

    if (!isValid) {
      return 'Needs attention';
    }

    return this.formGroup.dirty ? 'Ready to save' : 'No changes';
  }

  private formatResetWarning(impact: AssignmentResetImpact): string {
    const studentText = impact.StudentCount === 1 ? '1 student' : `${impact.StudentCount} students`;
    const lessonText = impact.LessonCount === 1 ? '1 lesson' : `${impact.LessonCount} lessons`;

    return `${lessonText} will reset for ${studentText}`;
  }

  private resolveMissingRequiredFields(): string[] {
    const fieldLabels: { [key: string]: string } = {
      AssignmentDescription: 'Description',
      AssignmentTypeId: 'Assignment type',
      CourseId: 'Course',
      DueDateTime: 'Due date',
      FlightSchoolId: 'Flight school',
      InstructorId: 'Instructor'
    };

    return Object.keys(this.formGroup.controls)
      .filter(controlName => this.formGroup.get(controlName)?.hasError('required'))
      .map(controlName => fieldLabels[controlName] || this.humanizeFieldName(controlName));
  }

  private humanizeFieldName(fieldName: string): string {
    return fieldName
      .replace(/Id$/, '')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/^./, value => value.toUpperCase());
  }
}
